// Formatters the admin console shares. Pure, and imported by `node --test`.
//
// Everything here answers "when, and for how long", which is most of what an operator's
// console is made of. Stamps are rendered in Pacific because every other day boundary in this
// product is Pacific — `briefs.brief_on`, the cron schedule, the Fivetran sync — and a console
// that showed UTC beside a Pacific brief day would invite exactly the off-by-one that
// `brief_on` already causes against Postgres' `current_date`.

const PACIFIC = 'America/Los_Angeles';

const STAMP_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: PACIFIC,
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: PACIFIC,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

// `9 Sep 02:30`. An unparseable stamp comes back as itself rather than as "Invalid Date",
// because a raw string is at least a clue and the other is noise.
export function formatStamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return STAMP_FORMAT.format(parsed);
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return TIME_FORMAT.format(parsed);
}

// A run's wall clock. Seconds under two minutes, then minutes — a cron that took 4m is worth
// reading at that resolution and one that took 241s is not.
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 120) return `${Math.round(seconds)}s`;
  return `${Math.round(seconds / 60)}m`;
}

// How a job run reads at a glance. Three states, and the middle one is the important one:
// a run with no finish never came back, which a table of exit codes alone would show as a
// blank cell rather than as the failure it is.
export type RunState = 'ok' | 'failed' | 'unfinished';

export function runState(run: {
  finished_at: string | null;
  exit_code: number | null;
}): RunState {
  if (run.finished_at == null) return 'unfinished';
  if (run.exit_code === 0) return 'ok';
  return 'failed';
}

export const RUN_STATE_LABEL: Record<RunState, string> = {
  ok: 'exit 0',
  failed: 'failed',
  unfinished: 'never finished',
};

// Semantic tone, separate from the accent hue. `warn` rather than `bad` for a run that never
// came back: it may still be running, and colouring an in-flight job as broken trains people
// to ignore the colour.
export const RUN_STATE_TONE: Record<RunState, 'ok' | 'warn' | 'bad'> = {
  ok: 'ok',
  failed: 'bad',
  unfinished: 'warn',
};

// How many strategists got the facts-only brief on the latest `write_briefs` run (P4-05), or 0.
// The job records `degraded` and `degraded_owners` on its outcome; a degraded run exits 0 (a
// WARNING), so this line on the status strip is what carries it to a person.
export function briefsDegradedCount(latest: { job_name: string; outcome: Record<string, unknown> }[]): number {
  const run = latest.find(item => item.job_name === 'write_briefs');
  if (!run || run.outcome.degraded !== true) return 0;
  const owners = run.outcome.degraded_owners;
  return Array.isArray(owners) && owners.length > 0 ? owners.length : 1;
}

// How a knock's `written` reads. Null is not false: the row predates the field, and calling
// that a held write would report a guard that was not there.
export type WrittenState = 'written' | 'held' | 'unknown';

export function writtenState(written: boolean | null): WrittenState {
  if (written === null) return 'unknown';
  return written ? 'written' : 'held';
}

export const WRITTEN_LABEL: Record<WrittenState, string> = {
  written: 'in Salesforce',
  held: 'not written',
  unknown: 'unrecorded',
};

export const WRITTEN_TONE: Record<WrittenState, 'ok' | 'warn' | 'bad'> = {
  written: 'ok',
  held: 'warn',
  unknown: 'warn',
};
